import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchproductDetailS,
  clearProductDetail,
} from "../redux/productDetailSlice";
import { checkPincode, resetPincodeState } from "../redux/pincodeSlice";
import { addToCart } from "../../../Checkout/Cart/redux/cartSlice";

import {
  useLocation,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import toast from "react-hot-toast";
import { getProductPdpUrl } from "../../../../utils/url.utils";
import { addToWishlist, removeFromWishlist } from "../../../../components/Wishlist/WishlistSlice";
import { useCartWishlist } from "../../../../app/CartWishlistContext";

function useProductContent(productSlugProp) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { itemSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getCartWishlistCount } = useCartWishlist();
  const skuFromUrl = searchParams.get("sku");

  const productSlug =
    productSlugProp || location.state?.product || itemSlug || null;

  const { data, loading, error } = useSelector(
    (state) => state.productDetail
  );

  const { pinset, pinloading, pinerror } = useSelector(
    (state) => state.pincode
  );

  /* ===============================
     STATES
  =============================== */

  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [availProductMessage, setAvailMessage] = useState(null);

  const [selectedColor, setSelectedColor] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [unit, setUnit] = useState("cm");

  const [pincode, setPincode] = useState("");
  const [pincodeChecked, setPincodeChecked] = useState(false);
  const [pincodeDetails, setPincodeDetails] = useState({});
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const collection = data?.collection || "";

  const prevKeyRef = useRef(null);

  /* ===============================
     FETCH PRODUCT
  =============================== */

  useEffect(() => {
    if (!productSlug) return;

    const key = `${productSlug}_${skuFromUrl || ""}`;

    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;

      setLocalLoading(true);
      setProductDetails(null);
      setSelectedSize(null);
      setSelectedColor(null);
      setAvailMessage(null);
      dispatch(
        fetchproductDetailS({
          slug: productSlug,
          sku: skuFromUrl,
        })
      );
    }

    return () => {
      dispatch(clearProductDetail());
    };
  }, [dispatch, productSlug, skuFromUrl]);

  /* ===============================
     SET PRODUCT DATA
  =============================== */

  useEffect(() => {

    if (!data?.id) return;

    setProductDetails(data);
    if (!data.product_sizes?.length) return;

    const savedSizeId = localStorage.getItem("selectedSizeId");

    const size =
      data.product_sizes.find((s) => s.id === Number(savedSizeId)) ||
      data.product_sizes.find((s) => s.id === data.selected_size_id) ||
      data.product_sizes[0];

    //  URL sku match
    const color =
      size.product_colors?.find((c) => c.sku === skuFromUrl) ||
      size.product_colors?.find((c) => c.id === data.selected_color_id) ||
      size.product_colors?.[0];

    const productMessage = data.message ?? null;
    setAvailMessage(productMessage);

    setSelectedSize(size);

    // prevent auto jump
    setSelectedColor(color);
    const stock = data.avail_stock ?? 0;
    setQuantity(stock > 0 ? 1 : 0);

    setLocalLoading(false);

  }, [data, skuFromUrl]);


  /* =================== SKU CHANGE  ================== */

  const handleSkuChange = (newSku) => {
    if (!newSku || newSku === skuFromUrl) return;

    setSearchParams({ sku: newSku });

    dispatch(
      fetchproductDetailS({
        slug: productSlug,
        sku: newSku,
      })
    );
  };

  /* ==========  PINCODE ========= */

   useEffect(() => {
  // Load pincode from localStorage on first render 
      const storedPincode = localStorage.getItem("pincode");
      if (storedPincode) {
        setPincode(storedPincode);
        dispatch(checkPincode(storedPincode));
      }
    }, [dispatch]);

    useEffect(() => {
      if (pinset) {
        setPincodeChecked(true);
        setPincodeDetails(pinset);
      }
    }, [pinset]);

    useEffect(() => {
      if (pinerror) toast.error("Invalid or unavailable pincode");
    }, [pinerror]);

    const handleCheck = () => {
      if (!pincode.trim()) {
        toast.error("Enter valid pincode");
        return;
      }
      dispatch(checkPincode(pincode));
      localStorage.setItem("pincode", pincode);
    };

    const handleReset = () => {
      dispatch(resetPincodeState());
      setPincode("");
      setPincodeChecked(false);
      setPincodeDetails({});
      localStorage.removeItem("pincode");
    };

  /* ===========  ADD TO CART  ================ */

  const handleAddToCart = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }

    if (!pincodeChecked || !pincodeDetails?.is_active) {
      toast.error("Verify your pincode to delivery address");
      return;
    }

    if (quantity <= 0) {
      toast.error("Product out of stock");
      return;
    }

    dispatch(addToCart({ product_id: selectedColor?.id, quantity }))
      .unwrap()
      .then((res) => {
        toast.success(res.data.message);

        // refresh header cart/wishlist count
        getCartWishlistCount();
      })
      .catch(() => toast.error("Failed to add to cart"));
  };

  /* ===============================
     WISHLIST
  =============================== */

  const toggleWishlist = async (e, product) => {
    e.stopPropagation();

    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login", {
        state: {
          from: location.pathname,
          productId: product.id,
          action: "wishlist",
        },
      });
      return;
    }

    try {
      if (product.is_wishlisted) {
        const wishlistId = product.wishlist?.[0]?.wishlist_id;

        if (wishlistId) {
          await dispatch(removeFromWishlist(wishlistId)).unwrap();
        }
      } else {
        await dispatch(addToWishlist({ product_id: product.id })).unwrap();
      }

      //  refresh header cart/wishlist count
      getCartWishlistCount();

    } catch {
      toast.error("Something went wrong");
    }
  };

  /* ===============================
     SIZE SELECT
  ================================ */

  const handleSizeSelection = (size) => {
    if (!size || selectedSize?.id === size.id) return;
    // save size
    localStorage.setItem("selectedSizeId", size.id);
    const color = size?.product_colors?.[0] || null;
    // set size
    setSelectedSize(size);
    // set color
    setSelectedColor(color);
    // set image
    // reset quantity
    const stock = color?.avail_stock || 0;
    setQuantity(stock > 0 ? 1 : 0);

    // update URL
    const slug = size?.action_url || productDetails?.action_url;
    const sku = size?.sku;

    navigate(getProductPdpUrl(slug, sku), { replace: true });
  };

  /* ====================  COLOR SELECT  ====================== */

  const selectionColor = (color) => {
    if (!color) return;
    // if (selectedColor?.sku === color.sku) return;
    setSelectedColor(color);
    const stock = Number(color?.avail_stock) || 0;
    setQuantity(stock > 0 ? 1 : 0);
    navigate(
      getProductPdpUrl(color.action_url, color.sku),
      { replace: true }
    );
  };

  /* =============================== */

  const currentPrice = selectedSize?.price ?? data?.selling_price;
  const isPincodeLocked = pincodeChecked && pincodeDetails?.is_active;

  return {
    loading,
    error,
    productDetails,
    selectedSize,
    selectedColor,
    quantity,
    setQuantity,
    currentPrice,
    localLoading,
    unit,
    setUnit,
    sizeSelection: handleSizeSelection,
    selectionColor,
    pincode,
    setPincode,
    isPincodeLocked,
    handleCheck,
    pinloading,
    handleReset,
    pincodeDetails,
    handleAddToCart,
    toggleWishlist,
    showLoginPrompt,
    setShowLoginPrompt,
    setSelectedSize,
    setSelectedColor,
    handleSkuChange,
    collection,
    availProductMessage,
  };
}

export default useProductContent;